// printUtils.js
//
// Reusable in-browser printing helper. Opens a new window with the
// provided HTML content + minimal print styles and triggers the browser
// print dialog. The user prints directly without ever downloading a PDF.
//
// Designed to be content-agnostic so future printable docs (defects,
// feature requests, etc.) can reuse it without duplicating window-open
// boilerplate.

/**
 * Print arbitrary HTML content in a new window.
 *
 * @param {Object} options
 * @param {string} options.title         Page <title>, also used as h1 if no titleHtml.
 * @param {string} options.bodyHtml      Inner HTML to render in the print body.
 *                                       Can be plain text, rich HTML, ReactQuill output, etc.
 * @param {string} [options.metaHtml]    Optional small block above the main body
 *                                       (e.g. author, date, tags). Rendered in
 *                                       smaller gray text.
 * @param {string} [options.extraCss]    Optional CSS appended to the default style.
 */
export function printHtml({ title, bodyHtml, metaHtml = '', extraCss = '' }) {
  if (!bodyHtml) {
    console.warn('printHtml: no body content provided');
    return;
  }

  // IMPORTANT: do not pass 'noopener' or 'noreferrer' here. Those flags
  // are a security best-practice when opening *external* links, but they
  // sever the connection to the new window — `window.open` returns null
  // (by design) and `w.document.write` won't work. Since this window is
  // same-origin and we need to populate its document, the flags don't
  // apply.
  const w = window.open('', '_blank');
  if (!w) {
    // Popup blocked. Surface a useful error to the caller.
    alert('Could not open print window. Please allow popups for this site.');
    return;
  }

  const safeTitle = escapeHtml(title || 'Print');

  // Print-specific stylesheet. The @page rule tightens margins; everything
  // else is just light typographic defaults suitable for a printed page.
  const css = `
    @page { margin: 0.75in; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
                   Oxygen, Ubuntu, sans-serif;
      color: #111827;
      line-height: 1.5;
      margin: 0;
      padding: 1.5rem;
    }
    h1 { margin: 0 0 0.5rem 0; font-size: 1.5rem; }
    .print-meta {
      color: #6b7280;
      font-size: 0.85rem;
      margin-bottom: 1rem;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 0.75rem;
    }
    .print-body { font-size: 0.95rem; }
    /* ReactQuill content uses these classes; keep its formatting */
    .print-body img { max-width: 100%; height: auto; }
    .print-body pre {
      background: #f3f4f6;
      padding: 0.5rem;
      border-radius: 0.25rem;
      overflow-x: auto;
    }
    .print-body blockquote {
      border-left: 3px solid #d1d5db;
      padding-left: 0.75rem;
      color: #4b5563;
      margin: 0.75rem 0;
    }
    .print-body ul, .print-body ol { padding-left: 1.5rem; }
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
    }
    ${extraCss}
  `;

  w.document.open();
  w.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <style>${css}</style>
</head>
<body>
  <h1>${safeTitle}</h1>
  ${metaHtml ? `<div class="print-meta">${metaHtml}</div>` : ''}
  <div class="print-body">${bodyHtml}</div>
  <script>
    // Wait a tick for images/styles to settle, then print.
    window.onload = function () {
      setTimeout(function () { window.print(); }, 100);
    };
  </script>
</body>
</html>`);
  w.document.close();
}

/** Minimal HTML-escape for plain text fields like title. */
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
